import { useState, useEffect } from "react";
import { Download, FileText } from "lucide-react";
import { useTranslation } from "../../../context/TranslationContext";
import { useAuth } from "../../../context/AuthContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, InfoBox, FormField, FormSelect, Button } from "../../common";
import toast from "react-hot-toast";
import { loadExcelJS } from "../../../utils/lazyImports";
import { getPendingDesiderata, PendingDesiderataGridItem } from "../../../lib/api/desiderata";
import { API_URL } from "../../../lib/api/config";

interface DesiderataReportModalProps {
  onClose: () => void;
}

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  editingStatus?: string;
}

export function DesiderataReportModal({ onClose }: DesiderataReportModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (initialLoad) {
      loadInitialPeriod();
    } else {
      loadPeriods(year);
    }
  }, [year]);

  const loadInitialPeriod = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      // Try current year first
      let foundYear = currentYear.toString();
      let response = await fetch(`${API_URL}/sites/AZJP/periods/${currentYear}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let data = null;
      if (response.ok) {
        data = await response.json();
        const openDesiderataPeriod = data.periods?.find(
          (p: Period) => p.editingStatus === "open-desiderata"
        );

        if (openDesiderataPeriod) {
          setYear(foundYear);
          setPeriods(data.periods || []);
          setSelectedPeriod(openDesiderataPeriod.id);
          setInitialLoad(false);
          return;
        }
      }

      // If not found in current year, try next year
      foundYear = nextYear.toString();
      response = await fetch(`${API_URL}/sites/AZJP/periods/${nextYear}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        data = await response.json();
        const openDesiderataPeriod = data.periods?.find(
          (p: Period) => p.editingStatus === "open-desiderata"
        );

        if (openDesiderataPeriod) {
          setYear(foundYear);
          setPeriods(data.periods || []);
          setSelectedPeriod(openDesiderataPeriod.id);
          setInitialLoad(false);
          return;
        }
      }

      // If no open-desiderata period found, default to current year's first period
      await loadPeriods(currentYear.toString());
      setInitialLoad(false);
    } catch (error) {
      console.error("Failed to load initial period:", error);
      setInitialLoad(false);
    }
  };

  const loadPeriods = async (targetYear: string) => {
    try {
      const response = await fetch(`${API_URL}/sites/AZJP/periods/${targetYear}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPeriods(data.periods || []);

        // Try to find open-desiderata period first
        const openDesiderataPeriod = data.periods?.find(
          (p: Period) => p.editingStatus === "open-desiderata"
        );

        if (openDesiderataPeriod) {
          setSelectedPeriod(openDesiderataPeriod.id);
        } else if (data.periods && data.periods.length > 0) {
          setSelectedPeriod(data.periods[0].id);
        } else {
          setSelectedPeriod("");
        }
      }
    } catch (error) {
      console.error("Failed to load periods:", error);
      setPeriods([]);
      setSelectedPeriod("");
    }
  };

  const loadUserNames = async () => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const users = await response.json();
        const names: Record<string, string> = {};
        users.forEach((u: any) => {
          names[u.id] = `${u.firstName} ${u.lastName}`;
        });
        setUserNames(names);
      }
    } catch (error) {
      console.error("Failed to load user names:", error);
    }
  };

  useEffect(() => {
    loadUserNames();
  }, []);

  const exportToExcel = async () => {
    if (!selectedPeriod || !token) {
      toast.error(t("reports.selectPeriod"));
      return;
    }

    setIsLoading(true);
    try {
      // Dynamically load ExcelJS only when needed
      const ExcelJS = await loadExcelJS();
      
      const data = await getPendingDesiderata(token, year, selectedPeriod);

      if (!data.grid || data.grid.length === 0) {
        toast.error(t("reports.noDataAvailable"));
        setIsLoading(false);
        return;
      }

      const grid = data.grid;
      const userIds = Object.keys(grid[0]).filter(
        (key) => key !== "date" && key !== "total"
      );
      
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const periodName = periods.find((p) => p.id === selectedPeriod)?.name || selectedPeriod;
      const sheetName = `${periodName.substring(0, 25)}`;
      const worksheet = workbook.addWorksheet(sheetName);
      
      // Set up the headers
      const headers = [
        t("reports.date"),
        ...userIds.map(userId => userNames[userId] || userId),
        t("reports.total")
      ];
      
      // Add headers to worksheet
      worksheet.addRow(headers);
      
      // Format header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE6E6E6' }
        };
        cell.border = {
          bottom: { style: 'thin' }
        };
      });
      
      // Add data rows
      grid.forEach((row: PendingDesiderataGridItem) => {
        const rowData = [
          row.date,
          ...userIds.map(userId => {
            const value = row[userId];
            return typeof value === "string" && value !== "" ? "X" : "";
          }),
          row.total
        ];
        worksheet.addRow(rowData);
      });
      
      // Set column widths
      worksheet.getColumn(1).width = 12; // Date column
      userIds.forEach((_, index) => {
        worksheet.getColumn(index + 2).width = 20; // User columns
      });
      worksheet.getColumn(userIds.length + 2).width = 10; // Total column
      
      // Generate the Excel file
      const fileName = `Desiderata_${year}_${periodName.replace(/[^a-zA-Z0-9]/g, "_")}.xlsx`;
      
      // Create a buffer and save it as a file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(t("reports.exportSuccess"));
    } catch (error) {
      console.error("Failed to export desiderata:", error);
      toast.error(t("reports.exportFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear + i - 2).toString());

  return (
    <ModalWrapper onClose={onClose} size="md">
      <ModalHeader
        icon={<FileText />}
        title={t("reports.desiderataReport")}
        onClose={onClose}
      />

      <ModalBody>
        <InfoBox variant="info" icon={<FileText />} title={t("reports.desiderataReportDescription")}>
          {t("reports.desiderataReportInstructions")}
        </InfoBox>

        <FormField label={t("common.year")}>
          <FormSelect
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              setInitialLoad(false);
            }}
            options={years.map((y) => ({ value: y, label: y }))}
          />
        </FormField>

        <FormField label={t("reports.period")}>
          <FormSelect
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            options={
              periods.length === 0
                ? [{ value: "", label: t("reports.noPeriodsAvailable"), disabled: true }]
                : periods.map((period) => ({ value: period.id, label: period.name }))
            }
            disabled={periods.length === 0}
          />
        </FormField>
      </ModalBody>

      <ModalFooter alignment="right">
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={exportToExcel}
          disabled={isLoading || !selectedPeriod}
          loading={isLoading}
          icon={<Download />}
        >
          {t("reports.exportToExcel")}
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
}
